'use client';

import { useState, useEffect, useRef } from 'react';

export default function LocationWidget({ onLocationChange, showShippingInfo = true, initialSelectedValues = null, initialLocationData = null }) {
  const [state, setState] = useState({
    province: [],
    city: [],
    district: [],
    subdistrict: []
  });

  const [selectedValues, setSelectedValues] = useState({
    province: initialSelectedValues?.province || '',
    city: initialSelectedValues?.city || '',
    district: initialSelectedValues?.district || '',
    subdistrict: initialSelectedValues?.subdistrict || '',
    postcode: initialSelectedValues?.postcode || ''
  });

  // Sync selectedValues when initialSelectedValues changes (for customer data search)
  useEffect(() => {
    if (initialSelectedValues) {
      const hasNewProvince = initialSelectedValues.province && 
                            initialSelectedValues.province !== selectedValues.province;
      const hasNewCity = initialSelectedValues.city && 
                        initialSelectedValues.city !== selectedValues.city;
      const hasNewDistrict = initialSelectedValues.district && 
                            initialSelectedValues.district !== selectedValues.district;
      const hasNewSubdistrict = initialSelectedValues.subdistrict && 
                                initialSelectedValues.subdistrict !== selectedValues.subdistrict;
      
      // Only update if there are actual changes and we have province data
      if (hasNewProvince || hasNewCity || hasNewDistrict || hasNewSubdistrict) {
        console.log('🔄 LocationWidget: Syncing selectedValues from initialSelectedValues', {
          old: selectedValues,
          new: initialSelectedValues
        });
        
        // Reset hasRestored to allow restoration with new data
        setHasRestored(false);
        
        // Update selectedValues to match initialSelectedValues
        setSelectedValues({
          province: initialSelectedValues.province || '',
          city: initialSelectedValues.city || '',
          district: initialSelectedValues.district || '',
          subdistrict: initialSelectedValues.subdistrict || '',
          postcode: initialSelectedValues.postcode || ''
        });
      }
    }
  }, [initialSelectedValues?.province, initialSelectedValues?.city, initialSelectedValues?.district, initialSelectedValues?.subdistrict]);

  const [locationData, setLocationData] = useState({
    id_location: initialLocationData?.id_location || null,
    courier: initialLocationData?.courier || '',
    paymentMethod: initialLocationData?.paymentMethod || '',
    leadtime: initialLocationData?.leadtime || '',
    postcode: initialLocationData?.postcode || '',
    destinationCode: initialLocationData?.destinationCode || '',
    hub_shortname: initialLocationData?.hub_shortname || ''
  });

  const [isRestoring, setIsRestoring] = useState(false);
  const [hasRestored, setHasRestored] = useState(false);

  const [loading, setLoading] = useState({
    province: false,
    city: false,
    district: false,
    subdistrict: false,
    location: false
  });

  const [errors, setErrors] = useState({
    province: null,
    city: null,
    district: null,
    subdistrict: null,
    location: null
  });

  // Default office ID
  const officeId = 1;

  // Helper function to wait for React state updates
  // Uses a ref to access current state value (avoids closure stale state issue)
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const waitForStateUpdate = async (checkFn, maxWait = 2000) => {
    const startTime = Date.now();
    const pollInterval = 50; // Check every 50ms
    
    while (Date.now() - startTime < maxWait) {
      // Use stateRef.current to get the latest state value
      if (checkFn(stateRef.current)) {
        return true;
      }
      await new Promise(resolve => setTimeout(resolve, pollInterval));
    }
    
    console.warn('⚠️ waitForStateUpdate: Timeout waiting for state update');
    return false;
  };

  // Load provinces on component mount
  useEffect(() => {
    loadProvinces();
  }, []);

  // Sync selectedValues when initialSelectedValues changes (for customer data search)
  useEffect(() => {
    if (initialSelectedValues && initialSelectedValues.province) {
      // Case-insensitive comparison
      const currentProvince = (selectedValues.province || '').toLowerCase().trim();
      const newProvince = (initialSelectedValues.province || '').toLowerCase().trim();
      
      // Only update if province actually changed
      if (currentProvince !== newProvince && newProvince.length > 0) {
        console.log('🔄 LocationWidget: Syncing selectedValues from initialSelectedValues', {
          old: selectedValues,
          new: initialSelectedValues
        });
        
        // Reset hasRestored to allow restoration with new data
        setHasRestored(false);
        
        // Update selectedValues to match initialSelectedValues
        setSelectedValues({
          province: initialSelectedValues.province || '',
          city: initialSelectedValues.city || '',
          district: initialSelectedValues.district || '',
          subdistrict: initialSelectedValues.subdistrict || '',
          postcode: initialSelectedValues.postcode || ''
        });
      }
    }
  }, [initialSelectedValues?.province, initialSelectedValues?.city, initialSelectedValues?.district, initialSelectedValues?.subdistrict]);

  // Restore location data when initial values are provided
  useEffect(() => {
    const restoreLocationData = async () => {
      // Don't restore if no initial values or provinces not loaded
      if (!initialSelectedValues || !initialSelectedValues.province) {
        return;
      }

      // Check if we have a province value
      const hasProvince = initialSelectedValues.province && initialSelectedValues.province.length > 0;
      if (!hasProvince) {
        return;
      }

      // Allow restoration if:
      // 1. We haven't restored yet, OR
      // 2. The initialSelectedValues changed (e.g., customer data loaded from orders)
      // Use case-insensitive comparison for province matching
      const currentProvince = (selectedValues.province || '').toLowerCase().trim();
      const newProvince = (initialSelectedValues.province || '').toLowerCase().trim();
      const isNewData = currentProvince !== newProvince;
      
      if (hasRestored && !isNewData) {
        return; // Already restored with same data
      }

      // Reset hasRestored if we're getting new data (for customer data search)
      if (isNewData) {
        setHasRestored(false);
      }

      // Wait for provinces to be loaded
      if (state.province.length === 0 || loading.province) {
        return;
      }

      setIsRestoring(true);
      setHasRestored(true);
      try {
        // Step 1: Find exact province match from dropdown (case-insensitive)
        const provinceMatch = state.province.find(
          p => p.province.toLowerCase().trim() === initialSelectedValues.province.toLowerCase().trim()
        );
        const exactProvince = provinceMatch ? provinceMatch.province : initialSelectedValues.province;
        
        console.log('🔄 LocationWidget: Restoring location data', {
          requestedProvince: initialSelectedValues.province,
          exactProvince,
          availableProvinces: state.province.length
        });
        
        // Step 2: Load cities
        if (exactProvince) {
          await loadCities(exactProvince);
          
          // Wait for cities to be populated in state
          console.log('⏳ Waiting for cities to load...');
          const citiesLoaded = await waitForStateUpdate((currentState) => currentState.city.length > 0);
          
          if (!citiesLoaded) {
            console.warn('⚠️ Cities did not load in time');
          }
          
          // Get current state after waiting
          const currentState = stateRef.current;
          console.log('📋 Loaded cities:', currentState.city.length, currentState.city.map(c => c.city).slice(0, 5));
          
          // Step 3: Find exact city match from dropdown (case-insensitive)
          const requestedCity = (initialSelectedValues.city || '').toLowerCase().trim();
          const cityMatch = currentState.city.find(
            c => c.city.toLowerCase().trim() === requestedCity
          );
          const exactCity = cityMatch ? cityMatch.city : initialSelectedValues.city;
          
            console.log('🔍 City matching:', {
              requested: initialSelectedValues.city,
              found: cityMatch ? cityMatch.city : 'NOT FOUND',
              exactCity,
              totalCities: currentState.city.length
            });
          
          // Step 4: Load districts if city exists
          if (exactCity) {
            await loadDistricts(exactProvince, exactCity);
            
            // Wait for districts to be populated in state
            console.log('⏳ Waiting for districts to load...');
            const districtsLoaded = await waitForStateUpdate((currentState) => currentState.district.length > 0);
            
            if (!districtsLoaded) {
              console.warn('⚠️ Districts did not load in time');
            }
            
            // Get current state after waiting
            const currentStateDistricts = stateRef.current;
            console.log('📋 Loaded districts:', currentStateDistricts.district.length, currentStateDistricts.district.map(d => d.district).slice(0, 5));
            
            // Step 5: Find exact district match
            const requestedDistrict = (initialSelectedValues.district || '').toLowerCase().trim();
            const districtMatch = currentStateDistricts.district.find(
              d => d.district.toLowerCase().trim() === requestedDistrict
            );
            const exactDistrict = districtMatch ? districtMatch.district : initialSelectedValues.district;
            
            console.log('🔍 District matching:', {
              requested: initialSelectedValues.district,
              found: districtMatch ? districtMatch.district : 'NOT FOUND',
              exactDistrict,
              totalDistricts: currentStateDistricts.district.length
            });
            
            // Step 6: Load subdistricts if district exists
            if (exactDistrict) {
              await loadSubdistricts(exactProvince, exactCity, exactDistrict);
              
              // Wait for subdistricts to be populated in state
              console.log('⏳ Waiting for subdistricts to load...');
              const subdistrictsLoaded = await waitForStateUpdate((currentState) => currentState.subdistrict.length > 0);
              
              if (!subdistrictsLoaded) {
                console.warn('⚠️ Subdistricts did not load in time');
              }
              
              // Get current state after waiting
              const currentStateSubdistricts = stateRef.current;
              console.log('📋 Loaded subdistricts:', currentStateSubdistricts.subdistrict.length, currentStateSubdistricts.subdistrict.map(s => s.subdistrict).slice(0, 5));
              
              // Step 7: Find exact subdistrict match
              const requestedSubdistrict = (initialSelectedValues.subdistrict || '').toLowerCase().trim();
              const subdistrictMatch = currentStateSubdistricts.subdistrict.find(
                s => s.subdistrict.toLowerCase().trim() === requestedSubdistrict
              );
              const exactSubdistrict = subdistrictMatch ? subdistrictMatch.subdistrict : initialSelectedValues.subdistrict;
              
              console.log('🔍 Subdistrict matching:', {
                requested: initialSelectedValues.subdistrict,
                found: subdistrictMatch ? subdistrictMatch.subdistrict : 'NOT FOUND',
                exactSubdistrict,
                totalSubdistricts: currentStateSubdistricts.subdistrict.length
              });
              
              // Step 8: Load location data if subdistrict exists
              if (exactSubdistrict) {
                const loadedLocationData = await loadLocationData(
                  exactProvince,
                  exactCity,
                  exactDistrict,
                  exactSubdistrict
                );
                
                // Update selected values to match exact dropdown values
                setSelectedValues({
                  province: exactProvince,
                  city: exactCity,
                  district: exactDistrict,
                  subdistrict: exactSubdistrict,
                  postcode: initialSelectedValues.postcode || loadedLocationData?.postcode || ''
                });

                // Call onLocationChange with restored data (use loadedLocationData if available, otherwise initialLocationData)
                if (onLocationChange && (loadedLocationData || initialLocationData)) {
                  onLocationChange({
                    id_location: loadedLocationData?.id_location || initialLocationData?.id_location || null,
                    province: exactProvince,
                    city: exactCity,
                    district: exactDistrict,
                    subdistrict: exactSubdistrict,
                    postcode: initialSelectedValues.postcode || loadedLocationData?.postcode || initialLocationData?.postcode || '',
                    courier: loadedLocationData?.courier || initialLocationData?.courier || '',
                    paymentMethod: loadedLocationData?.paymentMethod || initialLocationData?.paymentMethod || '',
                    leadtime: loadedLocationData?.leadtime || initialLocationData?.leadtime || '',
                    destinationCode: loadedLocationData?.destinationCode || initialLocationData?.destinationCode || '',
                    hub_shortname: loadedLocationData?.hub_shortname || initialLocationData?.hub_shortname || ''
                  });
                }
              } else {
                // Update selected values even if subdistrict is missing
                setSelectedValues({
                  province: exactProvince,
                  city: exactCity,
                  district: exactDistrict,
                  subdistrict: '',
                  postcode: initialSelectedValues.postcode || ''
                });
              }
            } else {
              // Update selected values for province, city, district only
              setSelectedValues({
                province: exactProvince,
                city: exactCity,
                district: '',
                subdistrict: '',
                postcode: initialSelectedValues.postcode || ''
              });
            }
          } else {
            // Update selected values for province, city only
            setSelectedValues({
              province: exactProvince,
              city: '',
              district: '',
              subdistrict: '',
              postcode: initialSelectedValues.postcode || ''
            });
          }
        } else {
          // Update selected values for province only
          setSelectedValues({
            province: exactProvince,
            city: '',
            district: '',
            subdistrict: '',
            postcode: initialSelectedValues.postcode || ''
          });
        }
      } catch (error) {
        console.error('Error restoring location data:', error);
        // Clear all selections on error to maintain consistency
        setSelectedValues({
          province: '',
          city: '',
          district: '',
          subdistrict: '',
          postcode: ''
        });
        setLocationData({
          id_location: null,
          courier: '',
          paymentMethod: '',
          leadtime: '',
          postcode: '',
          destinationCode: '',
          hub_shortname: ''
        });
        // Clear saved location data from localStorage
        if (typeof window !== 'undefined' && window.localStorage) {
          localStorage.removeItem('checkoutLocationData');
        }
        setErrors(prev => ({ ...prev, location: 'Gagal memuat data lokasi yang tersimpan' }));
      } finally {
        setIsRestoring(false);
      }
    };

    restoreLocationData();
  }, [initialSelectedValues, initialLocationData, state.province.length, loading.province, hasRestored]);

  const loadProvinces = async () => {
    setLoading(prev => ({ ...prev, province: true }));
    setErrors(prev => ({ ...prev, province: null }));
    try {
      const response = await fetch(`/api/widget/location?type=province&officeId=${officeId}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const provinces = await response.json();
      if (Array.isArray(provinces)) {
        setState(prev => ({ ...prev, province: provinces }));
      }
    } catch (error) {
      console.error('Error loading provinces:', error);
      setErrors(prev => ({ ...prev, province: error.message }));
      // Fallback to basic provinces if API fails
      setState(prev => ({
        ...prev,
        province: [
          { province: 'DKI Jakarta' },
          { province: 'Jawa Barat' },
          { province: 'Jawa Tengah' },
          { province: 'Jawa Timur' }
        ]
      }));
    } finally {
      setLoading(prev => ({ ...prev, province: false }));
    }
  };

  const loadCities = async (provinceName) => {
    if (!provinceName) return;
    
    setLoading(prev => ({ ...prev, city: true }));
    setErrors(prev => ({ ...prev, city: null }));
    try {
      const response = await fetch(`/api/widget/location?type=city&officeId=${officeId}&province=${encodeURIComponent(provinceName)}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const cities = await response.json();
      if (Array.isArray(cities)) {
        setState(prev => ({
          ...prev,
          city: cities,
          district: [],
          subdistrict: []
        }));
      }
    } catch (error) {
      console.error('Error loading cities:', error);
      setErrors(prev => ({ ...prev, city: error.message }));
      // Fallback cities
      setState(prev => ({
        ...prev,
        city: [
          { city: 'Jakarta Pusat' },
          { city: 'Jakarta Selatan' },
          { city: 'Bandung' },
          { city: 'Surabaya' }
        ],
        district: [],
        subdistrict: []
      }));
    } finally {
      setLoading(prev => ({ ...prev, city: false }));
    }
  };

  const loadDistricts = async (provinceName, cityName) => {
    if (!provinceName || !cityName) return;
    
    setLoading(prev => ({ ...prev, district: true }));
    setErrors(prev => ({ ...prev, district: null }));
    try {
      const response = await fetch(`/api/widget/location?type=district&officeId=${officeId}&province=${encodeURIComponent(provinceName)}&city=${encodeURIComponent(cityName)}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const districts = await response.json();
      if (Array.isArray(districts)) {
        setState(prev => ({
          ...prev,
          district: districts,
          subdistrict: []
        }));
      }
    } catch (error) {
      console.error('Error loading districts:', error);
      setErrors(prev => ({ ...prev, district: error.message }));
      // Fallback districts
      setState(prev => ({
        ...prev,
        district: [
          { district: 'Menteng' },
          { district: 'Kemang' },
          { district: 'Senayan' }
        ],
        subdistrict: []
      }));
    } finally {
      setLoading(prev => ({ ...prev, district: false }));
    }
  };

  const loadSubdistricts = async (provinceName, cityName, districtName) => {
    if (!provinceName || !cityName || !districtName) return;
    
    setLoading(prev => ({ ...prev, subdistrict: true }));
    setErrors(prev => ({ ...prev, subdistrict: null }));
    try {
      const response = await fetch(`/api/widget/location?type=subdistrict&officeId=${officeId}&province=${encodeURIComponent(provinceName)}&city=${encodeURIComponent(cityName)}&district=${encodeURIComponent(districtName)}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const subdistricts = await response.json();
      if (Array.isArray(subdistricts)) {
        setState(prev => ({ ...prev, subdistrict: subdistricts }));
      }
    } catch (error) {
      console.error('Error loading subdistricts:', error);
      setErrors(prev => ({ ...prev, subdistrict: error.message }));
      // Fallback subdistricts
      setState(prev => ({
        ...prev,
        subdistrict: [
          { subdistrict: 'Menteng', postcode: '10310' },
          { subdistrict: 'Kemang', postcode: '12560' },
          { subdistrict: 'Senayan', postcode: '10270' }
        ]
      }));
    } finally {
      setLoading(prev => ({ ...prev, subdistrict: false }));
    }
  };

  const loadLocationData = async (provinceName, cityName, districtName, subdistrictName) => {
    if (!provinceName || !cityName || !districtName || !subdistrictName) return null;
    
    setLoading(prev => ({ ...prev, location: true }));
    setErrors(prev => ({ ...prev, location: null }));
    try {
      const response = await fetch(`/api/widget/location?type=location&officeId=${officeId}&province=${encodeURIComponent(provinceName)}&city=${encodeURIComponent(cityName)}&district=${encodeURIComponent(districtName)}&subdistrict=${encodeURIComponent(subdistrictName)}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const locationInfo = await response.json();
      
      if (locationInfo && !locationInfo.error) {
        const paymentMethod = (locationInfo.bank_transfer == 1 || locationInfo.bank_transfer === '1') ? 'TRANSFER' : 'COD';
        
        const newLocationData = {
          id_location: locationInfo.id_location || null,
          courier: locationInfo.courier_name || 'JNE',
          paymentMethod: paymentMethod,
          leadtime: String(locationInfo.courier_name || '').toLowerCase() === 'lion'
            ? (locationInfo.leadtime ?? '')
            : (locationInfo.leadtime || '3-5'),
          postcode: locationInfo.postcode || '',
          destinationCode: locationInfo.destination_code || '',
          hub_shortname: locationInfo.hub_shortname || ''
        };
        
        setLocationData(newLocationData);
        return newLocationData;
      }
    } catch (error) {
      console.error('Error loading location data:', error);
      setErrors(prev => ({ ...prev, location: error.message }));
      // Fallback location data
      const fallbackData = {
        id_location: null,
        courier: 'JNE',
        paymentMethod: 'COD',
        leadtime: '3-5',
        postcode: '12560',
        destinationCode: 'JK001',
        hub_shortname: ''
      };
      setLocationData(fallbackData);
      return fallbackData;
    } finally {
      setLoading(prev => ({ ...prev, location: false }));
    }
    
    return null;
  };

  const handleChange = async (field, value) => {
    setSelectedValues(prev => ({ ...prev, [field]: value }));

    if (field === 'province') {
      setSelectedValues(prev => ({
        ...prev,
        city: '',
        district: '',
        subdistrict: '',
        postcode: ''
      }));
      setLocationData(prev => ({ ...prev, id_location: null, courier: '', paymentMethod: '', leadtime: '', postcode: '', destinationCode: '' }));
      setState(prev => ({
        ...prev,
        city: [],
        district: [],
        subdistrict: []
      }));
      if (value) await loadCities(value);
    } else if (field === 'city') {
      setSelectedValues(prev => ({
        ...prev,
        district: '',
        subdistrict: '',
        postcode: ''
      }));
      setLocationData(prev => ({ ...prev, id_location: null, courier: '', paymentMethod: '', leadtime: '', postcode: '', destinationCode: '' }));
      setState(prev => ({
        ...prev,
        district: [],
        subdistrict: []
      }));
      if (value && selectedValues.province) await loadDistricts(selectedValues.province, value);
    } else if (field === 'district') {
      setSelectedValues(prev => ({
        ...prev,
        subdistrict: '',
        postcode: ''
      }));
      setLocationData(prev => ({ ...prev, id_location: null, courier: '', paymentMethod: '', leadtime: '', postcode: '', destinationCode: '' }));
      setState(prev => ({
        ...prev,
        subdistrict: []
      }));
      if (value && selectedValues.province && selectedValues.city) {
        await loadSubdistricts(selectedValues.province, selectedValues.city, value);
      }
    } else if (field === 'subdistrict') {
      // Auto-fill postcode from subdistrict data
      const selectedSubdistrict = state.subdistrict.find(sub => sub.subdistrict === value);
      const postcode = selectedSubdistrict?.postcode || '';
      
      // Immediately update the postcode in the state
      setSelectedValues(prev => ({
        ...prev,
        subdistrict: value,
        postcode: postcode // Set postcode from subdistrict data
      }));

      if (value && selectedValues.province && selectedValues.city && selectedValues.district) {
        const loadedLocationData = await loadLocationData(selectedValues.province, selectedValues.city, selectedValues.district, value);
        
        // If we got a postcode from the location data, use it, otherwise keep the one from subdistrict
        const finalPostcode = loadedLocationData?.postcode || postcode;
        
        // Update both states with the final postcode
        setSelectedValues(prev => ({
          ...prev,
          postcode: finalPostcode
        }));
        
        setLocationData(prev => ({
          ...prev,
          ...loadedLocationData,
          postcode: finalPostcode
        }));

        onLocationChange({
          id_location: loadedLocationData?.id_location || null,
          province: selectedValues.province,
          city: selectedValues.city,
          district: selectedValues.district,
          subdistrict: value,
          postcode: finalPostcode,
          courier: loadedLocationData?.courier || '',
          paymentMethod: loadedLocationData?.paymentMethod || '',
          leadtime: loadedLocationData?.leadtime || '',
          destinationCode: loadedLocationData?.destinationCode || ''
        });
      } else {
        // Clear location data if subdistrict is cleared
        setLocationData(prev => ({ ...prev, id_location: null, courier: '', paymentMethod: '', leadtime: '', postcode: '', destinationCode: '' }));
        setSelectedValues(prev => ({
          ...prev,
          postcode: ''
        }));
      }
    }
  };

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Province */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Provinsi <span className="text-red-500">*</span>
          </label>
          <select
            value={selectedValues.province}
            onChange={(e) => handleChange('province', e.target.value)}
            disabled={loading.province}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${errors.province ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-green-500'} disabled:bg-gray-100`}
            required
          >
            <option value="">
              {loading.province ? 'Memuat provinsi...' : 
               errors.province ? 'Gagal memuat provinsi' : 'Pilih Provinsi'}
            </option>
            {state.province.map((prov, index) => (
              <option key={index} value={prov.province}>
                {prov.province}
              </option>
            ))}
          </select>
          {errors.province && (
            <div className="mt-1 text-sm text-red-600">
              ⚠️ {errors.province}
              <button
                type="button"
                onClick={loadProvinces}
                className="ml-2 text-blue-600 hover:text-blue-800 underline"
              >
                Coba Lagi
              </button>
            </div>
          )}
        </div>

        {/* City */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Kota/Kabupaten <span className="text-red-500">*</span>
          </label>
          <select
            value={selectedValues.city}
            onChange={(e) => handleChange('city', e.target.value)}
            disabled={!selectedValues.province || loading.city}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${errors.city ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-green-500'} disabled:bg-gray-100`}
            required
          >
            <option value="">
              {loading.city ? 'Memuat kota...' : 
               errors.city ? 'Gagal memuat kota' :
               !selectedValues.province ? 'Pilih Provinsi terlebih dahulu' : 'Pilih Kota/Kabupaten'}
            </option>
            {state.city.map((city, index) => (
              <option key={index} value={city.city}>
                {city.city}
              </option>
            ))}
          </select>
          {errors.city && (
            <div className="mt-1 text-sm text-red-600">
              ⚠️ {errors.city}
              <button
                type="button"
                onClick={() => loadCities(selectedValues.province)}
                className="ml-2 text-blue-600 hover:text-blue-800 underline"
              >
                Coba Lagi
              </button>
            </div>
          )}
        </div>

        {/* District */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Kecamatan <span className="text-red-500">*</span>
          </label>
          <select
            value={selectedValues.district}
            onChange={(e) => handleChange('district', e.target.value)}
            disabled={!selectedValues.city || loading.district}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${errors.district ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-green-500'} disabled:bg-gray-100`}
            required
          >
            <option value="">
              {loading.district ? 'Memuat kecamatan...' : 
               errors.district ? 'Gagal memuat kecamatan' :
               !selectedValues.city ? 'Pilih Kota terlebih dahulu' : 'Pilih Kecamatan'}
            </option>
            {state.district.map((district, index) => (
              <option key={index} value={district.district}>
                {district.district}
              </option>
            ))}
          </select>
          {errors.district && (
            <div className="mt-1 text-sm text-red-600">
              ⚠️ {errors.district}
              <button
                type="button"
                onClick={() => loadDistricts(selectedValues.province, selectedValues.city)}
                className="ml-2 text-blue-600 hover:text-blue-800 underline"
              >
                Coba Lagi
              </button>
            </div>
          )}
        </div>

        {/* Subdistrict */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Kelurahan <span className="text-red-500">*</span>
          </label>
          <select
            value={selectedValues.subdistrict}
            onChange={(e) => handleChange('subdistrict', e.target.value)}
            disabled={!selectedValues.district || loading.subdistrict}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${errors.subdistrict ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-green-500'} disabled:bg-gray-100`}
            required
          >
            <option value="">
              {loading.subdistrict ? 'Memuat kelurahan...' : 
               errors.subdistrict ? 'Gagal memuat kelurahan' :
               !selectedValues.district ? 'Pilih Kecamatan terlebih dahulu' : 'Pilih Kelurahan'}
            </option>
            {state.subdistrict.map((sub, index) => (
              <option key={index} value={sub.subdistrict}>
                {sub.subdistrict}
              </option>
            ))}
          </select>
          {errors.subdistrict && (
            <div className="mt-1 text-sm text-red-600">
              ⚠️ {errors.subdistrict}
              <button
                type="button"
                onClick={() => loadSubdistricts(selectedValues.province, selectedValues.city, selectedValues.district)}
                className="ml-2 text-blue-600 hover:text-blue-800 underline"
              >
                Coba Lagi
              </button>
            </div>
          )}
        </div>

        {/* Postcode */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Kode Pos <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={selectedValues.postcode}
            readOnly
            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 focus:outline-none focus:ring-2 focus:ring-green-500"
            placeholder="Kode pos akan terisi otomatis"
            required
          />
        </div>
      </div>



      {/* Location Data Error Display */}
      {errors.location && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <h3 className="text-sm font-medium text-red-800 mb-2">Gagal Memuat Informasi Pengiriman</h3>
          <div className="text-sm text-red-700">
            <p>⚠️ {errors.location}</p>
            <button
              type="button"
              onClick={() => loadLocationData(selectedValues.province, selectedValues.city, selectedValues.district, selectedValues.subdistrict)}
              className="mt-2 text-blue-600 hover:text-blue-800 underline"
            >
              Coba muat ulang informasi pengiriman
            </button>
          </div>
        </div>
      )}
    </div>
  );
}